import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChampionshipsAPI, PlayersAPI, TeamsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SiteHeader from "@/components/SiteHeader";
import QRCodeCard from "@/components/PlayerQRCode";
import { useAuth } from "@/contexts/AuthContext";
import OrganizerAssignCard from "@/components/OrganizerAssignCard";
import ImageInput from "@/components/ImageInput";
import VideoInput from "@/components/VideoInput";
import PlayerPicker from "@/components/PlayerPicker";
import SectionOrderCard from "@/components/SectionOrderCard";
import AppearancePanel from "@/components/AppearancePanel";
import ScoringEditor from "@/components/ScoringEditor";
import { SPORTS, SPORT_IDS, getSport, sportTerms, effectiveScoring, computePoints, positionKeys, derivePositionStats, SCORING_LABELS } from "@/lib/sports";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const SectionTitle = ({ children }) => (
  <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight pt-2">
    {children}
  </h3>
);

const Hint = ({ children }) => (
  <p className="text-zinc-500 font-body text-sm -mt-4">{children}</p>
);

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="text-overline !text-zinc-400">
      {label}
    </Label>
    {children}
  </div>
);

const detectVideoType = (url) =>
  /youtube\.com|youtu\.be/.test(url) ? "youtube" : /vimeo\.com/.test(url) ? "vimeo" : "url";

const empty = {
  name: "",
  sport: "football",
  season: "",
  subtitle: "",
  description: "",
  logo_url: "",
  cover_image_url: "",
  primary_color: "#D4AF37",
  standings: [],
  player_ids: [],
  guest_players: [],
  stats: {},
  gallery: [],
  highlights: [],
  best_moments: [],
  awards: [],
  team_awards: [],
  sponsors: [],
  appearance: {},
  scoring: {},
  section_order: [],
};

export default function AdminChampionship() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const isEdit = !!id && id !== "new";
  const [form, setForm] = useState(empty);
  const [allPlayers, setAllPlayers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    PlayersAPI.list().then(setAllPlayers).catch(() => {});
    TeamsAPI.list().then(setAllTeams).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    ChampionshipsAPI.get(id)
      .then((d) => {
        const sc = effectiveScoring(d.sport || "football", d.scoring);
        const standings = (d.standings || []).map((t) => {
          const auto = computePoints(t, sc);
          return {
            ...t,
            points_manual: t.points_manual || (auto !== null && auto !== (t.points || 0)),
          };
        });
        setForm({ ...empty, ...d, standings, stats: { ...(d.stats || {}) } });
      })
      .catch(() => toast.error("Championnat introuvable"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStat = (k, v) =>
    setForm((f) => ({ ...f, stats: { ...f.stats, [k]: Number(v) || 0 } }));

  // ----- generic list helpers -----
  const addItem = (key, item) =>
    setForm((f) => ({ ...f, [key]: [...f[key], { id: crypto.randomUUID(), ...item }] }));
  const updateItem = (key, i, patch) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((x, idx) => (idx === i ? { ...x, ...patch } : x)),
    }));
  const removeItem = (key, i) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const updateStanding = (i, patch) =>
    setForm((f) => ({
      ...f,
      standings: f.standings.map((t, idx) => {
        if (idx !== i) return t;
        let next = { ...t, ...patch };
        const sc = effectiveScoring(f.sport, f.scoring);
        if (!sc) return next;
        if (getSport(f.sport).position_scoring && Object.keys(patch).some((k) => /^p\d+$/.test(k)))
          next = { ...next, ...derivePositionStats(next) };
        if ("points" in patch) return { ...next, points_manual: true };
        if (patch.points_manual === false)
          return { ...next, points_manual: false, points: computePoints(next, sc) };
        if (!next.points_manual && Object.keys(patch).some((k) => k in sc))
          return { ...next, points: computePoints(next, sc) };
        return next;
      }),
    }));

  const setScoring = (sc) =>
    setForm((f) => {
      const eff = effectiveScoring(f.sport, sc);
      return {
        ...f,
        scoring: sc,
        standings: eff
          ? f.standings.map((t) => (t.points_manual ? t : { ...t, points: computePoints(t, eff) }))
          : f.standings,
      };
    });

  const recomputeAllPoints = () =>
    setForm((f) => {
      const eff = effectiveScoring(f.sport, f.scoring);
      if (!eff) return f;
      return {
        ...f,
        standings: f.standings.map((t) => ({ ...t, points_manual: false, points: computePoints(t, eff) })),
      };
    });

  const togglePlayer = (pid) =>
    setForm((f) => ({
      ...f,
      player_ids: f.player_ids.includes(pid)
        ? f.player_ids.filter((x) => x !== pid)
        : [...f.player_ids, pid],
    }));

  const linkTeam = (i, teamId) => {
    if (teamId === "none") {
      updateItem("standings", i, { team_id: null });
      return;
    }
    const t = allTeams.find((x) => x.id === teamId);
    if (!t) return;
    updateItem("standings", i, {
      team_id: t.id,
      name: t.name,
      logo_url: t.logo_url || "",
      primary_color: t.primary_color || "#0066FF",
    });
  };

  const sportPilots = allPlayers.filter((p) => (p.sport || "football") === form.sport);
  const linkPilot = (i, pid) => {
    if (pid === "none") return updateStanding(i, { player_id: null });
    const p = allPlayers.find((x) => x.id === pid);
    if (!p) return;
    updateStanding(i, {
      player_id: p.id,
      name: p.name,
      ...(p.hero_image_url ? { logo_url: p.hero_image_url } : {}),
    });
  };

  const linkAwardPlayer = (i, pid) => {
    if (pid === "none") {
      updateItem("awards", i, { player_id: null });
      return;
    }
    const p = allPlayers.find((x) => x.id === pid);
    if (!p) return;
    updateItem("awards", i, {
      player_id: p.id,
      player_name: p.name,
      team_name: p.club_name || "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error("Le nom du championnat est requis");
      return;
    }
    setSaving(true);
    try {
      const result = isEdit
        ? await ChampionshipsAPI.update(id, form)
        : await ChampionshipsAPI.create(form);
      toast.success(isEdit ? "Championnat mis à jour" : "Championnat créé");
      navigate(`/championship/${result.slug}`);
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer ce championnat ?")) return;
    try {
      await ChampionshipsAPI.remove(id);
      toast.success("Championnat supprimé");
      navigate("/championships");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  const sportDef = getSport(form.sport);
  const terms = sportTerms(form.sport);
  const sportTeams = allTeams.filter((t) => (t.sport || "football") === form.sport);
  const teamNames = form.standings.map((t) => t.name).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-16">
        <Link
          to="/championships"
          data-testid="admin-championship-back"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>

        <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-2">
          {isEdit ? "Éditer" : "Nouveau"} <span style={{ color: "#D4AF37" }}>championnat</span>
        </h1>
        <p className="text-zinc-400 font-body mb-10">
          L'archive officielle de l'événement, gravée sur le trophée. Classement final,
          résultats, récompenses, photos et vidéos — pour des années.
        </p>

        {isEdit && form.slug && isAdmin && (
          <div className="mb-10">
            <QRCodeCard
              url={`${window.location.origin}/championship/${form.slug}`}
              title={form.name}
              subtitle={form.season ? `Saison ${form.season}` : form.subtitle}
              fileBase={`championship-${form.slug}`}
              primaryColor={form.primary_color || "#D4AF37"}
            />
          </div>
        )}

        {isEdit && (
          <OrganizerAssignCard
            organizerId={form.organizer_id}
            onAssign={async (orgId) => {
              const updated = await ChampionshipsAPI.assignOrganizer(id, orgId);
              setForm((f) => ({
                ...f,
                organizer_id: updated.organizer_id,
                organizer_email: updated.organizer_email,
              }));
            }}
          />
        )}

        <datalist id="champ-team-names">
          {teamNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>

        <form onSubmit={submit} className="space-y-8" data-testid="admin-championship-form">
          {/* ---------- Sport ---------- */}
          <SectionTitle>Sport</SectionTitle>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {SPORT_IDS.map((s) => {
              const sport = SPORTS[s];
              const active = (form.sport || "football") === s;
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => setForm((f) => ({ ...f, sport: s, scoring: {} }))}
                  data-testid={`c-select-sport-${s}`}
                  data-active={active}
                  className="metal-pill px-3 py-2.5 rounded-md text-[10px] uppercase tracking-[0.22em] font-bold"
                  style={{ "--metal-accent": sport.color }}
                >
                  {sport.label}
                </button>
              );
            })}
          </div>

          {/* ---------- Identité ---------- */}
          <SectionTitle>Identité</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom du championnat *" htmlFor="c-name">
              <Input
                id="c-name"
                data-testid="c-input-name"
                className={inputClass}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Championnat Genevois"
                required
              />
            </Field>
            <Field label="Saison" htmlFor="c-season">
              <Input
                id="c-season"
                data-testid="c-input-season"
                className={inputClass}
                value={form.season}
                onChange={(e) => set("season", e.target.value)}
                placeholder="2025-2026"
              />
            </Field>
            <Field label="Sous-titre" htmlFor="c-subtitle">
              <Input
                id="c-subtitle"
                className={inputClass}
                value={form.subtitle}
                onChange={(e) => set("subtitle", e.target.value)}
                placeholder="L'archive officielle de la saison"
              />
            </Field>
            <Field label="Couleur d'accent" htmlFor="c-color">
              <div className="flex gap-2 items-center">
                <Input
                  id="c-color"
                  type="color"
                  className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                />
                <Input
                  className={inputClass}
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                  placeholder="#D4AF37"
                />
              </div>
            </Field>
            <Field label="Couleur du nom du championnat" htmlFor="c-title-color">
              <div className="flex gap-2 items-center">
                <Input
                  id="c-title-color"
                  type="color"
                  className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                  value={form.appearance?.title_color || "#D4AF37"}
                  onChange={(e) => set("appearance", { ...form.appearance, title_color: e.target.value })}
                  data-testid="c-title-color"
                />
                <Input
                  className={inputClass}
                  value={form.appearance?.title_color || ""}
                  onChange={(e) => set("appearance", { ...form.appearance, title_color: e.target.value })}
                  placeholder="Vide = dégradé doré"
                />
                {form.appearance?.title_color && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-zinc-400 hover:text-white shrink-0 px-2"
                    onClick={() => set("appearance", { ...form.appearance, title_color: "" })}
                    data-testid="c-title-color-reset"
                  >
                    Réinit
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Couleur du sous-titre" htmlFor="c-subtitle-color">
              <div className="flex gap-2 items-center">
                <Input
                  id="c-subtitle-color"
                  type="color"
                  className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                  value={form.appearance?.subtitle_color || "#d4d4d8"}
                  onChange={(e) => set("appearance", { ...form.appearance, subtitle_color: e.target.value })}
                  data-testid="c-subtitle-color"
                />
                <Input
                  className={inputClass}
                  value={form.appearance?.subtitle_color || ""}
                  onChange={(e) => set("appearance", { ...form.appearance, subtitle_color: e.target.value })}
                  placeholder="Vide = gris clair par défaut"
                />
                {form.appearance?.subtitle_color && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-zinc-400 hover:text-white shrink-0 px-2"
                    onClick={() => set("appearance", { ...form.appearance, subtitle_color: "" })}
                    data-testid="c-subtitle-color-reset"
                  >
                    Réinit
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Logo du championnat" htmlFor="c-logo">
              <ImageInput
                value={form.logo_url}
                onChange={(v) => set("logo_url", v)}
                testId="img-championship-logo"
                size="md"
                placeholder="https://..."
              />
            </Field>
            <Field label="Photo de couverture" htmlFor="c-cover">
              <ImageInput
                value={form.cover_image_url}
                onChange={(v) => set("cover_image_url", v)}
                testId="img-championship-cover"
                aspect="wide"
                placeholder="https://..."
              />
            </Field>
          </div>
          <Field label="Présentation de l'événement" htmlFor="c-description">
            <Textarea
              id="c-description"
              data-testid="c-input-description"
              className={`${inputClass} min-h-[120px]`}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Raconte l'histoire de ce championnat : le contexte, les moments forts, ce qui l'a rendu unique…"
            />
          </Field>

          {/* ---------- Design & Couleurs ---------- */}
          <SectionTitle>Design & Couleurs</SectionTitle>
          <Hint>
            Personnalise entièrement les couleurs de la page publique. Extrais une palette
            depuis le logo ou la couverture, puis ajuste manuellement.
          </Hint>
          <AppearancePanel
            appearance={form.appearance || {}}
            onChange={(v) => set("appearance", v)}
            imageUrl={form.logo_url || form.cover_image_url}
            accent={form.primary_color}
            onAccent={(v) => set("primary_color", v)}
            testIdPrefix="c-ap"
          />

          {/* ---------- Stats générales ---------- */}
          <SectionTitle>Statistiques générales</SectionTitle>
          <Hint>Stats adaptées au sport sélectionné.</Hint>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${Math.min(sportDef.tournament_stats.length, 4)}, minmax(0, 1fr))`,
            }}
          >
            {sportDef.tournament_stats.map((s) => (
              <Field key={s.key} label={s.label} htmlFor={`c-stat-${s.key}`}>
                <Input
                  id={`c-stat-${s.key}`}
                  data-testid={`c-stat-${s.key}`}
                  type="number"
                  className={inputClass}
                  placeholder="0"
                  value={form.stats[s.key] || ""}
                  onChange={(e) => setStat(s.key, e.target.value)}
                />
              </Field>
            ))}
          </div>

          {/* ---------- Équipes & classement final ---------- */}
          <SectionTitle>{terms.participants} & Classement final</SectionTitle>
          <Hint>
            {terms.individual
              ? "Lie un athlète existant ou saisis-le librement pour construire le classement individuel."
              : "Lie une équipe existante de la base (lien cliquable sur la page) ou saisis-la librement."}
          </Hint>
          <ScoringEditor
            sport={form.sport}
            scoring={form.scoring}
            onChange={setScoring}
            onRecomputeAll={recomputeAllPoints}
            testIdPrefix="c-scoring"
          />
          <div className="space-y-3">
            {form.standings.map((team, i) => (
              <div
                key={team.id || i}
                className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 space-y-2"
                data-testid={`c-team-row-${i}`}
              >
                <div className="grid sm:grid-cols-[auto,1fr,auto,40px] gap-3 items-start">
                  <ImageInput
                    value={team.logo_url || ""}
                    onChange={(v) => updateItem("standings", i, { logo_url: v })}
                    testId={`img-c-team-logo-${i}`}
                    size="sm"
                    placeholder="URL logo"
                  />
                  <div className="space-y-2">
                    <Input
                      className={inputClass}
                      placeholder={terms.namePlaceholder}
                      value={team.name}
                      onChange={(e) => updateItem("standings", i, { name: e.target.value })}
                    />
                    {sportTeams.length > 0 && (
                      <Select
                        value={team.team_id || "none"}
                        onValueChange={(v) => linkTeam(i, v)}
                      >
                        <SelectTrigger
                          className={`${inputClass} h-8 text-xs`}
                          data-testid={`c-team-link-${i}`}
                        >
                          <SelectValue placeholder="Lier à une équipe de la base" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                          <SelectItem value="none">Saisie libre (non liée)</SelectItem>
                          {sportTeams.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {terms.individual && sportPilots.length > 0 && (
                      <Select
                        value={team.player_id || "none"}
                        onValueChange={(v) => linkPilot(i, v)}
                      >
                        <SelectTrigger
                          className={`${inputClass} h-8 text-xs`}
                          data-testid={`c-pilot-link-${i}`}
                        >
                          <SelectValue placeholder="Lier à une fiche" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                          <SelectItem value="none">Saisie libre (non liée)</SelectItem>
                          {sportPilots.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <Input
                    type="color"
                    className={`${inputClass} h-10 w-14 p-1 cursor-pointer`}
                    value={team.primary_color || "#0066FF"}
                    onChange={(e) =>
                      updateItem("standings", i, { primary_color: e.target.value })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removeItem("standings", i)}
                    className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                    data-testid={`c-remove-team-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {positionKeys(form.sport).length > 0 && (
                  <div className="pt-1">
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                      Arrivées par position (nombre de courses terminées à chaque place)
                    </Label>
                    <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 mt-1">
                      {positionKeys(form.sport).map((k) => (
                        <div key={k}>
                          <div className="text-[9px] uppercase tracking-[0.12em] text-zinc-600 text-center">
                            {SCORING_LABELS[k] || k}
                          </div>
                          <Input
                            type="number"
                            min="0"
                            className={`${inputClass} h-8 px-1 text-center text-xs`}
                            placeholder="0"
                            value={team[k] || ""}
                            onChange={(e) => updateStanding(i, { [k]: Number(e.target.value) || 0 })}
                            data-testid={`c-pos-${k}-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div
                  className="grid gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${sportDef.standings_columns.length}, minmax(0, 1fr))`,
                  }}
                >
                  {sportDef.standings_columns.map(({ key, label }) => {
                    const isPts = key === "points";
                    const hasScoring = !!effectiveScoring(form.sport, form.scoring);
                    return (
                      <div key={key}>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                          {label}
                          {isPts && hasScoring && team.points_manual && (
                            <span className="text-amber-400 normal-case ml-1" title="Points saisis manuellement">manuel</span>
                          )}
                        </Label>
                        <Input
                          type="number"
                          className={inputClass}
                          placeholder="0"
                          value={team[key] || ""}
                          onChange={(e) =>
                            updateStanding(i, { [key]: Number(e.target.value) || 0 })
                          }
                          data-testid={isPts ? `c-points-${i}` : undefined}
                        />
                        {isPts && hasScoring && team.points_manual && (
                          <button
                            type="button"
                            onClick={() => updateStanding(i, { points_manual: false })}
                            className="mt-1 text-[9px] uppercase tracking-[0.14em] text-[#D4AF37] hover:underline"
                            data-testid={`c-points-auto-${i}`}
                          >
                            Auto
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-team"
              onClick={() =>
                addItem("standings", {
                  name: "",
                  logo_url: "",
                  primary_color: "#0066FF",
                  team_id: null,
                  matches_played: 0,
                  wins: 0,
                  draws: 0,
                  losses: 0,
                  points: 0,
                })
              }
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> {terms.add}
            </Button>
          </div>

          {/* ---------- Joueurs ---------- */}
          <SectionTitle>{terms.individual ? terms.entities : "Joueurs"}</SectionTitle>
          <Hint>
            {terms.individual
              ? `Sélectionne des ${terms.countLabel} de la base (fiche cliquable) et/ou ajoute des ${terms.countLabel} libres.`
              : "Sélectionne des joueurs de la base (fiche cliquable) et/ou ajoute des joueurs libres."}
          </Hint>
          {allPlayers.length > 0 && (
            <PlayerPicker
              players={allPlayers}
              selectedIds={form.player_ids}
              onToggle={togglePlayer}
              testIdPrefix="c-pick-player"
            />
          )}
          <div className="space-y-2">
            {form.guest_players.map((g, i) => (
              <div
                key={g.id || i}
                className="grid grid-cols-[1fr,1fr,1fr,40px] gap-2 items-center bg-[#0A0B0E] border border-white/5 rounded-md p-2"
                data-testid={`c-guest-player-${i}`}
              >
                <Input
                  className={inputClass}
                  placeholder={terms.individual ? terms.namePlaceholder : "Nom du joueur"}
                  value={g.name || ""}
                  onChange={(e) => updateItem("guest_players", i, { name: e.target.value })}
                />
                <Input
                  className={inputClass}
                  list="champ-team-names"
                  placeholder="Équipe"
                  value={g.team_name || ""}
                  onChange={(e) => updateItem("guest_players", i, { team_name: e.target.value })}
                />
                <Input
                  className={inputClass}
                  placeholder="Poste"
                  value={g.position || ""}
                  onChange={(e) => updateItem("guest_players", i, { position: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("guest_players", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`c-remove-guest-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-guest-player"
              onClick={() => addItem("guest_players", { name: "", team_name: "", position: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> {terms.individual ? `${terms.add} libre` : "Ajouter un joueur libre"}
            </Button>
          </div>

          {/* ---------- Récompenses individuelles ---------- */}
          <SectionTitle>Récompenses individuelles</SectionTitle>
          <Hint>Meilleur joueur, meilleur buteur, meilleur gardien…</Hint>
          <div className="space-y-2">
            {form.awards.map((a, i) => (
              <div
                key={a.id || i}
                className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 grid sm:grid-cols-[1fr,1fr,1fr,40px] gap-2 items-end"
                data-testid={`c-award-${i}`}
              >
                <Field label="Titre" htmlFor={`c-award-title-${i}`}>
                  <Input
                    id={`c-award-title-${i}`}
                    className={inputClass}
                    placeholder={sportDef.awards?.[1] || "Meilleur buteur"}
                    list="award-suggestions"
                    value={a.title || ""}
                    onChange={(e) => updateItem("awards", i, { title: e.target.value })}
                    data-testid={`c-award-title-input-${i}`}
                  />
                </Field>
                <Field label="Joueur" htmlFor={`c-award-player-${i}`}>
                  <div className="space-y-1.5">
                    {allPlayers.length > 0 && (
                      <Select
                        value={a.player_id || "none"}
                        onValueChange={(v) => linkAwardPlayer(i, v)}
                      >
                        <SelectTrigger
                          className={`${inputClass} h-8 text-xs`}
                          data-testid={`c-award-player-link-${i}`}
                        >
                          <SelectValue placeholder="Lier un joueur" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                          <SelectItem value="none">Saisie libre</SelectItem>
                          {allPlayers.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Input
                      id={`c-award-player-${i}`}
                      className={inputClass}
                      placeholder="Nom du joueur"
                      value={a.player_name || ""}
                      onChange={(e) => updateItem("awards", i, { player_name: e.target.value })}
                      data-testid={`c-award-player-name-${i}`}
                    />
                  </div>
                </Field>
                <Field label="Équipe" htmlFor={`c-award-team-${i}`}>
                  <Input
                    id={`c-award-team-${i}`}
                    className={inputClass}
                    list="champ-team-names"
                    placeholder="Équipe"
                    value={a.team_name || ""}
                    onChange={(e) => updateItem("awards", i, { team_name: e.target.value })}
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("awards", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`c-remove-award-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <datalist id="award-suggestions">
              {(sportDef.awards || []).map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <div className="flex flex-wrap gap-1.5">
              {(sportDef.awards || []).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() =>
                    addItem("awards", { title: t, player_id: null, player_name: "", team_name: "" })
                  }
                  className="text-[10px] uppercase tracking-[0.14em] font-bold border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full px-3 py-1.5"
                  data-testid={`c-award-suggestion-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  + {t}
                </button>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-award"
              onClick={() =>
                addItem("awards", { title: "", player_id: null, player_name: "", team_name: "" })
              }
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une récompense
            </Button>
          </div>

          {/* ---------- Récompenses d'équipe ---------- */}
          <SectionTitle>Récompenses d'équipe</SectionTitle>
          <Hint>Équipe fair-play, meilleure défense, équipe révélation…</Hint>
          <div className="space-y-2">
            {(form.team_awards || []).map((a, i) => (
              <div
                key={a.id || i}
                className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 grid sm:grid-cols-[1fr,1fr,1fr,40px] gap-2 items-end"
                data-testid={`c-team-award-${i}`}
              >
                <Field label="Titre" htmlFor={`c-team-award-title-${i}`}>
                  <Input
                    id={`c-team-award-title-${i}`}
                    className={inputClass}
                    list="team-award-suggestions"
                    placeholder="Équipe fair-play"
                    value={a.title || ""}
                    onChange={(e) => updateItem("team_awards", i, { title: e.target.value })}
                    data-testid={`c-team-award-title-input-${i}`}
                  />
                </Field>
                <Field label="Équipe" htmlFor={`c-team-award-team-${i}`}>
                  <Input
                    id={`c-team-award-team-${i}`}
                    className={inputClass}
                    list="champ-team-names"
                    placeholder="Nom de l'équipe"
                    value={a.team_name || ""}
                    onChange={(e) => updateItem("team_awards", i, { team_name: e.target.value })}
                    data-testid={`c-team-award-team-input-${i}`}
                  />
                </Field>
                <Field label="Note (facultatif)" htmlFor={`c-team-award-note-${i}`}>
                  <Input
                    id={`c-team-award-note-${i}`}
                    className={inputClass}
                    placeholder="0 carton sur la saison"
                    value={a.note || ""}
                    onChange={(e) => updateItem("team_awards", i, { note: e.target.value })}
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("team_awards", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`c-remove-team-award-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <div className="sm:col-span-4">
                  <Field label="Photo de l'équipe (facultatif)">
                    <ImageInput
                      value={a.photo_url}
                      onChange={(v) => updateItem("team_awards", i, { photo_url: v })}
                      testId={`c-team-award-photo-${i}`}
                    />
                  </Field>
                </div>
              </div>
            ))}
            <datalist id="team-award-suggestions">
              {["Équipe fair-play", "Meilleure attaque", "Meilleure défense", "Équipe révélation"].map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-team-award"
              onClick={() => addItem("team_awards", { title: "", team_name: "", note: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une récompense d'équipe
            </Button>
          </div>

          {/* ---------- Photos ---------- */}
          <SectionTitle>Photos</SectionTitle>
          <div className="space-y-3">
            {form.gallery.map((g, i) => (
              <div
                key={g.id || i}
                className="grid sm:grid-cols-[auto,1fr,40px] gap-3 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`c-gallery-${i}`}
              >
                <ImageInput
                  value={g.url || ""}
                  onChange={(v) => updateItem("gallery", i, { url: v })}
                  testId={`img-c-gallery-${i}`}
                  size="sm"
                  placeholder="URL photo"
                />
                <Input
                  className={inputClass}
                  placeholder="Légende (optionnelle)"
                  value={g.caption || ""}
                  onChange={(e) => updateItem("gallery", i, { caption: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("gallery", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`c-remove-gallery-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-gallery"
              onClick={() => addItem("gallery", { url: "", caption: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une photo
            </Button>
          </div>

          {/* ---------- Vidéos ---------- */}
          <SectionTitle>Vidéos</SectionTitle>
          <Hint>
            Colle un lien YouTube/Vimeo OU télécharge une vidéo depuis ton appareil (max
            200 MB, hébergée automatiquement).
          </Hint>
          <div className="space-y-3">
            {form.highlights.map((h, i) => (
              <div
                key={h.id || i}
                className="grid sm:grid-cols-[1fr,1.6fr,40px] gap-2 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`c-highlight-${i}`}
              >
                <Input
                  className={inputClass}
                  placeholder="Titre"
                  value={h.title || ""}
                  onChange={(e) => updateItem("highlights", i, { title: e.target.value })}
                />
                <VideoInput
                  value={h.url || ""}
                  onChange={(v, meta) =>
                    updateItem("highlights", i, {
                      url: v,
                      type: meta?.uploaded ? "url" : detectVideoType(v),
                    })
                  }
                  testId={`c-highlight-video-${i}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("highlights", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`c-remove-highlight-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-highlight"
              onClick={() => addItem("highlights", { title: "", type: "youtube", url: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une vidéo
            </Button>
          </div>

          {/* ---------- Meilleurs moments ---------- */}
          <SectionTitle>Meilleurs moments</SectionTitle>
          <Hint>Les actions marquantes de l'événement (clips vidéo).</Hint>
          <div className="space-y-3">
            {form.best_moments.map((h, i) => (
              <div
                key={h.id || i}
                className="grid sm:grid-cols-[1fr,1.6fr,40px] gap-2 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`c-moment-${i}`}
              >
                <Input
                  className={inputClass}
                  placeholder="Titre (ex : But de la finale)"
                  value={h.title || ""}
                  onChange={(e) => updateItem("best_moments", i, { title: e.target.value })}
                />
                <VideoInput
                  value={h.url || ""}
                  onChange={(v, meta) =>
                    updateItem("best_moments", i, {
                      url: v,
                      type: meta?.uploaded ? "url" : detectVideoType(v),
                    })
                  }
                  testId={`c-moment-video-${i}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("best_moments", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`c-remove-moment-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-moment"
              onClick={() => addItem("best_moments", { title: "", type: "youtube", url: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter un moment
            </Button>
          </div>

          {/* ---------- Partenaires ---------- */}
          <SectionTitle>Partenaires / Sponsors</SectionTitle>
          <div className="space-y-3">
            {form.sponsors.map((s, i) => (
              <div
                key={s.id || i}
                className="grid sm:grid-cols-[auto,1fr,1fr,40px] gap-3 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`c-sponsor-${i}`}
              >
                <ImageInput
                  value={s.logo_url || ""}
                  onChange={(v) => updateItem("sponsors", i, { logo_url: v })}
                  testId={`img-c-sponsor-${i}`}
                  size="sm"
                  placeholder="URL logo"
                />
                <Input
                  className={inputClass}
                  placeholder="Nom du partenaire"
                  value={s.name || ""}
                  onChange={(e) => updateItem("sponsors", i, { name: e.target.value })}
                />
                <Input
                  className={inputClass}
                  placeholder="Site web (optionnel)"
                  value={s.website || ""}
                  onChange={(e) => updateItem("sponsors", i, { website: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("sponsors", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`c-remove-sponsor-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="c-add-sponsor"
              onClick={() => addItem("sponsors", { name: "", logo_url: "", website: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter un partenaire
            </Button>
          </div>

          {/* ---------- Ordre des sections ---------- */}
          <div className="space-y-3">
            <h3 className="font-display text-lg font-bold uppercase tracking-wide">
              Ordre des sections de la page
            </h3>
            <SectionOrderCard
              page="championship"
              value={form.section_order}
              onChange={(v) => set("section_order", v)}
            />
          </div>

          {/* ---------- Save bar ---------- */}
          <div className="sticky bottom-4 z-10">
            <div className="bg-[#0A0B0E]/90 backdrop-blur-xl border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="delete-championship"
                  onClick={onDelete}
                  className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-championship"
                className="text-black rounded-md px-6 h-11 font-body font-medium hover:opacity-90"
                style={{ backgroundColor: form.primary_color || "#D4AF37" }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer le championnat"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
